import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/pages/Leads";
import { toast } from "sonner";

const CUSTOM = "__custom__";
const NONE = "__none__";

const BLANK = {
  category_name: "", subcategory: "", name: "", size_spec: "", color: "", pack_size: "",
  hardware_type: "", gauge: "", length_ft: "", width_ft: "",
  content_per_unit: "", content_unit: "", unit: "Nos", quantity: "0", rate: "0",
  cost_basis: "unit", minimum_level: "0", reorder_level: "0", safety_stock: "0",
  maximum_level: "0", warehouse_id: "", aliases: "", item_type: "raw_material",
};

export default function NewItemDialog({ open, onOpenChange, warehouses, canEditCost, onCreated,
  onUseExisting }) {
  const [categories, setCategories] = useState([]);
  const [options, setOptions] = useState({ colors: [], pack_sizes: [], units: [], content_units: [] });
  const [form, setForm] = useState(BLANK);
  const [customUnit, setCustomUnit] = useState("");
  const [customColor, setCustomColor] = useState("");
  const [customPack, setCustomPack] = useState("");
  const [busy, setBusy] = useState(false);
  const [dupe, setDupe] = useState(null);

  const checkDuplicate = async (value) => {
    const name = (value ?? form.name ?? "").trim();
    if (name.length < 3) return setDupe(null);
    try {
      const { data } = await api.post("/inventory/check-duplicate", {
        name,
        warehouse_id: form.warehouse_id,
        color: form.color === CUSTOM ? customColor : form.color,
        pack_size: form.pack_size === CUSTOM ? customPack : form.pack_size,
      });
      setDupe(data.exists ? data : null);
    } catch {
      setDupe(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: o }] = await Promise.all([
        api.get("/inventory/categories?include_inactive=false"),
        api.get("/inventory/options"),
      ]);
      setCategories(c.categories);
      setOptions(o);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(BLANK);
    setCustomUnit("");
    setCustomColor("");
    setCustomPack("");
    setDupe(null);
    load();
  }, [open, load]);

  const category = useMemo(
    () => categories.find((c) => c.name === form.category_name),
    [categories, form.category_name]
  );
  const shows = (field) => (category?.fields || []).includes(field);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const subs = (category?.subcategories || []).filter((s) => s.status === "active");
  const unit = form.unit === CUSTOM ? customUnit : form.unit;

  const save = async () => {
    if (!form.name.trim()) return toast.error("Item name is required");
    if (!form.category_name) return toast.error("Choose a category");
    if (!form.warehouse_id) return toast.error("Choose where it is kept");
    if (!unit.trim()) return toast.error("Choose or type a unit");
    setBusy(true);
    try {
      const wh = warehouses.find((w) => w.id === form.warehouse_id);
      await api.post("/stock", {
        name: form.name.trim(),
        item_type: form.item_type,
        category_id: category?.id || "",
        category_name: form.category_name,
        subcategory: form.subcategory === NONE ? "" : form.subcategory,
        size_spec: shows("size_spec") ? form.size_spec : "",
        color: shows("color") ? (form.color === CUSTOM ? customColor : form.color) : "",
        pack_size: shows("pack_size") ? (form.pack_size === CUSTOM ? customPack : form.pack_size) : "",
        hardware_type: shows("hardware_type") ? form.hardware_type : "",
        gauge: shows("gauge") ? form.gauge : "",
        length_ft: shows("dimensions") ? Number(form.length_ft || 0) : 0,
        width_ft: shows("dimensions") ? Number(form.width_ft || 0) : 0,
        content_per_unit: shows("content") ? Number(form.content_per_unit || 0) : 0,
        content_unit: shows("content") ? (form.content_unit === NONE ? "" : form.content_unit) : "",
        unit: unit.trim(),
        warehouse_id: form.warehouse_id,
        warehouse_name: wh?.name || "",
        quantity: Number(form.quantity || 0),
        rate: Number(form.rate || 0),
        cost_basis: form.cost_basis,
        minimum_level: Number(form.minimum_level || 0),
        reorder_level: Number(form.reorder_level || 0),
        safety_stock: Number(form.safety_stock || 0),
        maximum_level: Number(form.maximum_level || 0),
        aliases: form.aliases ? form.aliases.split(",").map((a) => a.trim()).filter(Boolean) : [],
      });
      toast.success("Item added");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const contentTotal =
    Number(form.content_per_unit) && form.content_unit && form.content_unit !== NONE
      ? `${Number(form.quantity || 0) * Number(form.content_per_unit)} ${form.content_unit}`
      : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="new-item-dialog">
        <DialogHeader>
          <DialogTitle>Add Item</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Category">
            <Select value={form.category_name}
              onValueChange={(v) => setForm({ ...form, category_name: v, subcategory: "" })}>
              <SelectTrigger data-testid="item-category-select">
                <SelectValue placeholder="Choose category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Sub-category">
            <Select value={form.subcategory || NONE} onValueChange={(v) => set("subcategory", v)}
              disabled={subs.length === 0}>
              <SelectTrigger data-testid="item-subcategory-select">
                <SelectValue placeholder={subs.length ? "Choose sub-category" : "Not needed"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {subs.map((s) => (
                  <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Item name" className="sm:col-span-2">
            <Input data-testid="stock-name-input" placeholder="e.g. MS Square Pipe 40x40"
              value={form.name} onChange={(e) => set("name", e.target.value)}
              onBlur={(e) => checkDuplicate(e.target.value)} />
            {dupe && (
              <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-2.5"
                data-testid="duplicate-warning">
                <div className="text-xs font-medium text-amber-700">{dupe.message}</div>
                {dupe.matches.map((m) => (
                  <div key={m.item_id}
                    className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                    <span>
                      <span className="font-medium">{m.official_name}</span>
                      {m.matched_on !== m.official_name && (
                        <span className="text-muted-foreground"> — matched “{m.matched_on}”</span>
                      )}
                      <span className="text-muted-foreground">
                        {" "}• {m.quantity} {m.unit} • {m.warehouse_name}
                        {m.color || m.pack_size
                          ? ` • ${[m.color, m.pack_size].filter(Boolean).join(" ")}`
                          : ""}
                      </span>
                    </span>
                    <Button type="button" size="sm" variant="outline"
                      className="h-6 px-2 text-[10px]"
                      data-testid={`use-existing-${m.item_id}`}
                      onClick={() => {
                        onUseExisting?.(m.item_id);
                        onOpenChange(false);
                      }}>
                      Use existing item
                    </Button>
                  </div>
                ))}
                <button type="button" className="mt-1.5 text-[10px] text-muted-foreground underline"
                  data-testid="duplicate-dismiss" onClick={() => setDupe(null)}>
                  No, this is a different item — continue
                </button>
              </div>
            )}
          </Field>

          {shows("size_spec") && (
            <Field label='Size / specification'>
              <Input data-testid="item-size-input" placeholder='e.g. 1" x 1" x 20 ft'
                value={form.size_spec} onChange={(e) => set("size_spec", e.target.value)} />
            </Field>
          )}

          {shows("hardware_type") && (
            <Field label="Type">
              <Input data-testid="item-hardware-type-input" placeholder="e.g. Hex head, 4 inch"
                value={form.hardware_type} onChange={(e) => set("hardware_type", e.target.value)} />
            </Field>
          )}

          {shows("gauge") && (
            <Field label="Gauge / thickness">
              <Input data-testid="item-gauge-input" placeholder="e.g. 16 Gauge / 1.5 mm"
                value={form.gauge} onChange={(e) => set("gauge", e.target.value)} />
            </Field>
          )}

          {shows("dimensions") && (
            <>
              <Field label="Length (ft)">
                <Input data-testid="item-length-input" value={form.length_ft}
                  onChange={(e) => set("length_ft", e.target.value)} />
              </Field>
              <Field label="Width (ft)">
                <Input data-testid="item-width-input" value={form.width_ft}
                  onChange={(e) => set("width_ft", e.target.value)} />
              </Field>
            </>
          )}

          {shows("color") && (
            <Field label="Colour">
              <Select value={form.color} onValueChange={(v) => set("color", v)}>
                <SelectTrigger data-testid="item-color-select">
                  <SelectValue placeholder="Choose colour" />
                </SelectTrigger>
                <SelectContent>
                  {options.colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value={CUSTOM}>Custom colour…</SelectItem>
                </SelectContent>
              </Select>
              {form.color === CUSTOM && (
                <Input className="mt-1" data-testid="item-custom-color-input"
                  placeholder="Type the colour" value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)} />
              )}
            </Field>
          )}

          {shows("pack_size") && (
            <Field label="Pack size">
              <Select value={form.pack_size} onValueChange={(v) => set("pack_size", v)}>
                <SelectTrigger data-testid="item-pack-size-select">
                  <SelectValue placeholder="Choose pack size" />
                </SelectTrigger>
                <SelectContent>
                  {options.pack_sizes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  <SelectItem value={CUSTOM}>Custom pack size…</SelectItem>
                </SelectContent>
              </Select>
              {form.pack_size === CUSTOM && (
                <Input className="mt-1" data-testid="item-custom-pack-input"
                  placeholder="e.g. 15 L" value={customPack}
                  onChange={(e) => setCustomPack(e.target.value)} />
              )}
            </Field>
          )}

          <Field label="Unit (what you count)">
            <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger data-testid="stock-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                <SelectItem value={CUSTOM}>Custom unit…</SelectItem>
              </SelectContent>
            </Select>
            {form.unit === CUSTOM && (
              <Input className="mt-1" data-testid="item-custom-unit-input"
                placeholder="Type the unit" value={customUnit}
                onChange={(e) => setCustomUnit(e.target.value)} />
            )}
          </Field>

          <Field label="Opening quantity">
            <Input data-testid="stock-qty-input" value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)} />
          </Field>

          {shows("content") && (
            <div className="sm:col-span-2 rounded border border-dashed border-border p-3">
              <div className="overline mb-2">How much is inside one {unit || "unit"}? (optional)</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label={`Content in one ${unit || "unit"}`}>
                  <Input data-testid="stock-content-per-unit-input" placeholder="e.g. 35"
                    value={form.content_per_unit}
                    onChange={(e) => set("content_per_unit", e.target.value)} />
                </Field>
                <Field label="Content unit">
                  <Select value={form.content_unit || NONE} onValueChange={(v) => set("content_unit", v)}>
                    <SelectTrigger data-testid="stock-content-unit-select">
                      <SelectValue placeholder="Not needed" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not needed</SelectItem>
                      {options.content_units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Total (calculated)">
                  <Input readOnly data-testid="stock-content-total-preview" value={contentTotal} />
                </Field>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Example: 1 Drum = 35 Kg, so 5 Drums shows 175 Kg automatically.
              </p>
            </div>
          )}

          <Field label="Kept at (warehouse)">
            <Select value={form.warehouse_id} onValueChange={(v) => set("warehouse_id", v)}>
              <SelectTrigger data-testid="stock-warehouse-select">
                <SelectValue placeholder="Choose place" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Item type">
            <Select value={form.item_type} onValueChange={(v) => set("item_type", v)}>
              <SelectTrigger data-testid="stock-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["raw_material", "wip", "finished_good", "consumable", "spare_part"].map((t) => (
                  <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="sm:col-span-2 grid gap-3 sm:grid-cols-4">
            {[
              ["minimum_level", "Minimum", null],
              ["reorder_level", "Reorder at", "stock-reorder-input"],
              ["safety_stock", "Safety stock", null],
              ["maximum_level", "Maximum", null],
            ].map(([k, label, tid]) => (
              <Field key={k} label={label}>
                <Input data-testid={tid || `item-${k}-input`} value={form[k]}
                  onChange={(e) => set(k, e.target.value)} />
              </Field>
            ))}
          </div>

          {canEditCost && (
            <>
              <Field label="Rate ₹">
                <Input data-testid="stock-rate-input" value={form.rate}
                  onChange={(e) => set("rate", e.target.value)} />
              </Field>
              <Field label="Rate is per">
                <Select value={form.cost_basis} onValueChange={(v) => set("cost_basis", v)}>
                  <SelectTrigger data-testid="stock-cost-basis-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unit">Per {unit || "unit"}</SelectItem>
                    <SelectItem value="content">
                      Per {form.content_unit && form.content_unit !== NONE ? form.content_unit : "content unit"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}

          <Field label="Other names employees use (comma separated)" className="sm:col-span-2">
            <Input data-testid="stock-aliases-input" placeholder="resin, frp resin"
              value={form.aliases} onChange={(e) => set("aliases", e.target.value)} />
          </Field>
        </div>

        <DialogFooter>
          <Button data-testid="stock-save-button" onClick={save} disabled={busy}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
